import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { AvatarContainer, TeacherToggle } from '../components/AvatarContainer';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Send,
  Volume2,
  Loader2,
  CheckCircle,
  Target,
  BookOpen,
  Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonView() {
  const { lessonId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [lesson, setLesson] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('frau_rasha');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [lessonCompleted, setLessonCompleted] = useState(false);
  
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const [lessonRes, historyRes] = await Promise.all([
          axios.get(`${API}/lessons/${lessonId}`, { withCredentials: true }),
          axios.get(`${API}/chat/history/${lessonId}`, { withCredentials: true })
        ]);
        
        setLesson(lessonRes.data);
        setSelectedTeacher(lessonRes.data.teacher);
        
        if (historyRes.data.messages?.length > 0) {
          setMessages(historyRes.data.messages);
        } else {
          // Add initial greeting
          const greeting = lessonRes.data.teacher === 'frau_rasha'
            ? `Hallo! Willkommen zu unserer Lektion "${lessonRes.data.title_de}"! Ich bin Frau Rasha und freue mich, mit dir zu lernen. Let's start with something simple - how do you say "Hello" in German?`
            : `Guten Tag! Willkommen zur Lektion "${lessonRes.data.title_de}". Ich bin Herr Amr, und heute werden wir die Grammatik genau anschauen. Let me explain today's objective...`;
          
          setMessages([{
            role: 'assistant',
            content: greeting,
            teacher: lessonRes.data.teacher,
            timestamp: new Date().toISOString()
          }]);
        }
      } catch (error) {
        toast.error('Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };

    fetchLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setSending(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        lesson_id: lessonId,
        message: text,
        teacher: selectedTeacher
      }, { withCredentials: true });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        teacher: response.data.teacher,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  const handleVoiceTranscription = (text) => {
    sendMessage(text);
  };

  const playAudio = async (text, messageIndex) => {
    try {
      setPlayingAudio(messageIndex);
      
      const response = await axios.post(`${API}/speech/synthesize`, {
        text: text,
        voice: selectedTeacher === 'frau_rasha' ? 'nova' : 'onyx'
      }, { withCredentials: true });

      const audioBlob = await fetch(`data:audio/mp3;base64,${response.data.audio}`).then(r => r.blob());
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onended = () => setPlayingAudio(null);
        audioRef.current.play();
      }
    } catch (error) {
      toast.error('Failed to play audio');
      setPlayingAudio(null);
    }
  };

  const completeLesson = async () => {
    try {
      await axios.post(`${API}/progress/${lessonId}/complete`, {}, { withCredentials: true });
      setLessonCompleted(true);
      toast.success('Lesson completed! Points earned!');
    } catch (error) {
      toast.error('Failed to complete lesson');
    }
  };

  const getLessonTitle = () => {
    if (!lesson) return '';
    if (language === 'de') return lesson.title_de;
    if (language === 'ar') return lesson.title_ar;
    return lesson.title_en;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row" data-testid="lesson-view">
      {/* Hidden audio element */}
      <audio ref={audioRef} className="hidden" />

      {/* Left Panel - Teacher & Info */}
      <div className="lg:w-2/5 bg-slate-900 text-white p-6 lg:min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/lessons')}
            className="text-white hover:bg-white/10"
            data-testid="back-to-lessons"
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            {t('back')}
          </Button>
          <Badge className="bg-white/20">{lesson?.level}</Badge>
        </div>

        {/* Teacher Avatar */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <AvatarContainer 
            teacher={selectedTeacher} 
            size="xl" 
            speaking={sending}
            className="mb-6"
          />
          
          <TeacherToggle 
            selected={selectedTeacher} 
            onChange={setSelectedTeacher}
          />
        </div>

        {/* Lesson Info */}
        <Card className="bg-white/10 border-white/20 mt-6">
          <CardContent className="p-4">
            <h2 className="font-heading font-semibold text-lg mb-2">
              {getLessonTitle()}
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Target className="h-4 w-4 mt-1 text-teal-400" />
                <div>
                  <p className="text-sm font-medium text-white/80">{t('objectives')}</p>
                  <ul className="text-xs text-white/60 mt-1 space-y-1">
                    {lesson?.objectives?.map((obj, i) => (
                      <li key={i}>• {obj}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {!lessonCompleted && messages.length >= 4 && (
              <Button 
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600"
                onClick={completeLesson}
                data-testid="complete-lesson-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('finish')} (+{lesson?.points_reward} {t('points')})
              </Button>
            )}

            {lessonCompleted && (
              <div className="mt-4 p-3 rounded-lg bg-green-500/20 border border-green-500/30 text-center">
                <Trophy className="h-6 w-6 mx-auto mb-2 text-amber-400" />
                <p className="text-sm font-medium">Lesson Completed!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Panel - Chat */}
      <div className="lg:w-3/5 flex flex-col h-[60vh] lg:h-screen">
        {/* Chat Messages */}
        <ScrollArea className="flex-1 p-6">
          <div className="space-y-4 max-w-2xl mx-auto">
            {messages.map((message, index) => (
              <div
                key={message.id || `msg-${index}-${message.role}`}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`chat-bubble ${
                  message.role === 'user' 
                    ? 'chat-bubble-user' 
                    : 'chat-bubble-assistant'
                }`}>
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                      <img 
                        src={message.teacher === 'frau_rasha' 
                          ? 'https://images.unsplash.com/photo-1741868159744-159b83484687?w=32&h=32&fit=crop'
                          : 'https://images.unsplash.com/photo-1752486268262-6ce6b339a8de?w=32&h=32&fit=crop'
                        }
                        alt={message.teacher}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span>
                        {message.teacher === 'frau_rasha' ? t('frauRasha') : t('herrAmr')}
                      </span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  
                  {message.role === 'assistant' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-8 px-2"
                      onClick={() => playAudio(message.content, index)}
                      disabled={playingAudio !== null}
                      data-testid={`play-audio-${index}`}
                    >
                      {playingAudio === index ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Volume2 className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            {sending && (
              <div className="flex justify-start">
                <div className="chat-bubble chat-bubble-assistant">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="border-t border-border p-4 bg-card">
          <form onSubmit={handleSubmit} className="flex items-center gap-3 max-w-2xl mx-auto">
            <VoiceRecorder 
              onTranscription={handleVoiceTranscription}
              disabled={sending}
            />
            
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={t('typeMessage')}
              disabled={sending}
              className="flex-1"
              data-testid="message-input"
            />
            
            <Button 
              type="submit" 
              disabled={!inputMessage.trim() || sending}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="send-message-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
